import api from './api';

// Types para assinatura
export interface Subscription {
  id: string;
  external_id: string;
  plan: {
    id: string;
    name: string;
    description: string;
    amount: number;
    tokens_included: number;
    features: string[];
  };
  status: 'active' | 'cancelled' | 'expired' | 'pending';
  amount: number;
  tokens_included: number;
  next_billing_date: string;
  created_at: string;
  cancelled_at?: string;
  auto_renew: boolean;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  amount: number;
  tokens_included: number;
  billing_interval: string;
  features: string[];
  cost_per_1k_tokens: number;
  savings_percentage: number;
  is_popular: boolean;
  is_recommended: boolean;
  priority_support: boolean;
  premium_templates: boolean;
  exclusive_features: boolean;
  monthly_consultation: boolean;
  dedicated_support: boolean;
  beta_features: boolean;
}

export interface SubscriptionCreateData {
  plan_id: string;
  card_data: {
    number: string;
    name: string;
    expiry: string;
    cvc: string;
  };
  start_date?: string;
}

export interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
  cancelled_at: string;
  status: string;
}

/**
 * Obter todos os planos de assinatura disponíveis
 */
export const getSubscriptionPlans = async (): Promise<SubscriptionPlan[]> => {
  try {
    const response = await api.get('/subscriptions/plans');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar planos de assinatura:', error);
    throw error;
  }
};

/**
 * Obter assinatura ativa do usuário
 */
export const getMySubscription = async (): Promise<Subscription | null> => {
  try {
    const response = await api.get('/subscriptions/my-subscription');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar assinatura:', error);
    throw error;
  }
};

/**
 * Criar nova assinatura
 */
export const createSubscription = async (subscriptionData: SubscriptionCreateData): Promise<Subscription> => {
  try {
    const response = await api.post('/subscriptions/subscribe', subscriptionData);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar assinatura:', error);
    throw error;
  }
};

/**
 * Cancelar assinatura ativa
 */
export const cancelSubscription = async (): Promise<CancelSubscriptionResponse> => {
  try {
    const response = await api.post('/subscriptions/cancel');
    return response.data;
  } catch (error) {
    console.error('Erro ao cancelar assinatura:', error);
    throw error;
  }
};

/**
 * Fazer upgrade da assinatura
 */
export const upgradeSubscription = async (newPlanId: string): Promise<Subscription> => {
  try {
    const response = await api.post('/subscriptions/upgrade', {
      new_plan_id: newPlanId
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao fazer upgrade da assinatura:', error);
    throw error;
  }
};

/**
 * Obter histórico de faturas da assinatura
 */
export const getSubscriptionInvoices = async (): Promise<any[]> => {
  try {
    const response = await api.get('/subscriptions/invoices');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar faturas:', error);
    throw error;
  }
};

/**
 * Reativar assinatura cancelada
 */
export const reactivateSubscription = async (planId: string): Promise<Subscription> => {
  try {
    const response = await api.post('/subscriptions/reactivate', {
      plan_id: planId
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao reativar assinatura:', error);
    throw error;
  }
};

/**
 * Verificar se usuário pode cancelar assinatura
 */
export const canCancelSubscription = async (): Promise<boolean> => {
  try {
    const subscription = await getMySubscription();
    return subscription?.status === 'active' && subscription?.auto_renew === true;
  } catch (error) {
    console.error('Erro ao verificar se pode cancelar assinatura:', error);
    return false;
  }
};

/**
 * Obter informações de uso da assinatura
 */
export const getSubscriptionUsage = async (): Promise<any> => {
  try {
    const response = await api.get('/subscriptions/usage');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar uso da assinatura:', error);
    throw error;
  }
};

/**
 * Atualizar método de pagamento da assinatura
 */
export const updatePaymentMethod = async (cardData: any): Promise<Subscription> => {
  try {
    const response = await api.put('/subscriptions/payment-method', {
      card_data: cardData
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar método de pagamento:', error);
    throw error;
  }
};

/**
 * Verificar status da próxima cobrança
 */
export const getNextBillingInfo = async (): Promise<any> => {
  try {
    const response = await api.get('/subscriptions/next-billing');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar informações da próxima cobrança:', error);
    throw error;
  }
};

/**
 * Pausar assinatura temporariamente
 */
export const pauseSubscription = async (resumeDate?: string): Promise<Subscription> => {
  try {
    const response = await api.post('/subscriptions/pause', {
      resume_date: resumeDate
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao pausar assinatura:', error);
    throw error;
  }
};

/**
 * Retomar assinatura pausada
 */
export const resumeSubscription = async (): Promise<Subscription> => {
  try {
    const response = await api.post('/subscriptions/resume');
    return response.data;
  } catch (error) {
    console.error('Erro ao retomar assinatura:', error);
    throw error;
  }
};

/**
 * Obter detalhes completos da assinatura com histórico
 */
export const getSubscriptionDetails = async (): Promise<any> => {
  try {
    const response = await api.get('/subscriptions/details');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar detalhes da assinatura:', error);
    throw error;
  }
};

/**
 * Verificar se assinatura está próxima do vencimento
 */
export const checkSubscriptionExpiry = async (): Promise<{ isExpiring: boolean; daysUntilExpiry: number }> => {
  try {
    const subscription = await getMySubscription();
    if (!subscription || subscription.status !== 'active') {
      return { isExpiring: false, daysUntilExpiry: 0 };
    }

    const nextBilling = new Date(subscription.next_billing_date);
    const today = new Date();
    const diffTime = nextBilling.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      isExpiring: diffDays <= 7, // Considera próximo do vencimento se faltam 7 dias ou menos
      daysUntilExpiry: diffDays
    };
  } catch (error) {
    console.error('Erro ao verificar expiração da assinatura:', error);
    return { isExpiring: false, daysUntilExpiry: 0 };
  }
};

/**
 * Obter recomendações de upgrade baseadas no uso
 */
export const getUpgradeRecommendations = async (): Promise<any[]> => {
  try {
    const response = await api.get('/subscriptions/recommendations');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar recomendações de upgrade:', error);
    return [];
  }
};

export default {
  getSubscriptionPlans,
  getMySubscription,
  createSubscription,
  cancelSubscription,
  upgradeSubscription,
  getSubscriptionInvoices,
  reactivateSubscription,
  canCancelSubscription,
  getSubscriptionUsage,
  updatePaymentMethod,
  getNextBillingInfo,
  pauseSubscription,
  resumeSubscription,
  getSubscriptionDetails,
  checkSubscriptionExpiry,
  getUpgradeRecommendations
}; 